// The phone ticket list's next page, added to the cards already loaded. The list hears about one fetch several times
// (getpaginatedTickets writes the tickets, attachData writes them again, and attachSlas once more when the SLAs
// arrive), so appending on each write drew every page after the first three times. A card already in the list is
// updated where it stands - the later writes carry its SLA - and only new ones are added.
export const mergeTicketPage = <T extends { Id: number }>(existing: T[], incoming: T[]): T[] => {
  const byId = new Map(incoming.map(ticket => [ticket.Id, ticket]));
  const loaded = new Set(existing.map(ticket => ticket.Id));
  return [...existing.map(ticket => byId.get(ticket.Id) ?? ticket), ...incoming.filter(ticket => !loaded.has(ticket.Id))];
};

// Where a Refresh of the phone list stands: asked for, but the list not yet told it is loading ('requested'); loading
// ('inFlight'); or none out ('idle').
export type PhoneRefreshPhase = 'idle' | 'requested' | 'inFlight';

// What the phone list does with each write to the list state: keep the cards as they are, replace them all, or merge
// the written page into them. A Refresh replaces the cards only with its own answer - the tickets written while it
// was out are the last page fetched before it, and drawing those dropped every earlier page and put the answer's
// cards after them (2026-10-12). It is told apart by being a different tickets array from the one on screen when the
// Refresh began; the same array once loading has finished means the Refresh failed, and the cards stay.
export const phoneCardsUpdate = ({ phase, isRefreshing, ticketsChanged, page }: {
  phase: PhoneRefreshPhase;
  isRefreshing: boolean;
  ticketsChanged: boolean;
  page: number;
}): { action: 'keep' | 'replace' | 'merge'; phase: PhoneRefreshPhase } => {
  if (phase === 'requested') return { action: 'keep', phase: isRefreshing ? 'inFlight' : 'requested' };
  if (isRefreshing) return { action: 'keep', phase };
  if (phase === 'inFlight' && ticketsChanged) return { action: 'replace', phase: 'idle' };
  return { action: page !== 1 ? 'merge' : 'replace', phase: 'idle' };
};
